import React, { useState, useEffect } from 'react';
import { 
  HardDrive, 
  File, 
  FileVideo, 
  Upload, 
  Trash2,
  Info,
  Loader2,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import api from '../services/api';

import { formatDate } from '../utils/dateUtils';

interface StorageStats {
  totalStorageUsed: number;
  totalFiles: number;
  largestFile: {
    name: string;
    size: number;
    type: string;
  };
  storageByType: {
    videos: number;
    images: number;
    audio: number;
  };
  recentActivity: {
    id: string;
    fileName: string;
    action: 'upload' | 'delete';
    size: number;
    timestamp: Date;
  }[];
  companyDirectory: string;
  lastUpdated: Date;
}

interface StorageManagementProps {
  onClose: () => void;
}

const StorageManagement: React.FC<StorageManagementProps> = ({ onClose }) => {
  const [storageStats, setStorageStats] = useState<StorageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStorageStats = async () => {
    try {
      const response = await api.get('/storage/stats');
      const data = response.data;
      
      // Convert timestamp strings to Date objects
      const processedData = {
        ...data,
        lastUpdated: new Date(data.lastUpdated || Date.now()),
        recentActivity: data.recentActivity.map((activity: any) => ({
          ...activity,
          timestamp: new Date(activity.timestamp)
        }))
      };
      
      setStorageStats(processedData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStorageStats();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchStorageStats();
  };

  const formatFileSize = (bytes: number): string => {
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0 B';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  const getTypePercentage = (typeSize: number): number => {
    if (!storageStats || storageStats.totalStorageUsed === 0) return 0;
    return (typeSize / storageStats.totalStorageUsed) * 100;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Storage Management</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Loading storage information...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-xl">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Storage Management</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
              <span className="text-red-800">{error}</span>
              <button
                onClick={handleRefresh}
                className="ml-auto bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!storageStats) return null;

  return (
    <div className="bg-white rounded-lg shadow-xl">
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900">Storage Management</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-gray-600">View your file storage and disk usage information</p>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Storage Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Total Storage Used */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Storage Used</p>
                <p className="text-xl font-bold text-gray-900">{formatFileSize(storageStats.totalStorageUsed)}</p>
              </div>
              <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <HardDrive className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <div className="mt-2">
              <p className="text-xs text-gray-500">Across all asset files</p>
            </div>
          </div>

          {/* File Count */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Files</p>
                <p className="text-xl font-bold text-gray-900">{storageStats.totalFiles.toLocaleString()}</p>
              </div>
              <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                <File className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <div className="mt-2">
              <p className="text-xs text-gray-500">Assets across all types</p>
            </div>
          </div>

          {/* Largest File */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Largest File</p>
                <p className="text-xl font-bold text-gray-900">{formatFileSize(storageStats.largestFile.size)}</p>
              </div>
              <div className="h-10 w-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <FileVideo className="w-5 h-5 text-yellow-600" />
              </div>
            </div>
            <div className="mt-2">
              <p className="text-xs text-gray-500 truncate" title={storageStats.largestFile.name}>
                {storageStats.largestFile.name}
              </p>
            </div>
          </div>
        </div>

        {/* Storage Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* File Type Breakdown */}
          <div className="bg-white border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Storage by File Type</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-3" />
                  <span className="text-sm text-gray-600">Videos</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium text-gray-900">
                    {formatFileSize(storageStats.storageByType.videos)}
                  </span>
                  <span className="text-xs text-gray-500 ml-2">
                    {getTypePercentage(storageStats.storageByType.videos).toFixed(1)}%
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-3" />
                  <span className="text-sm text-gray-600">Images</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium text-gray-900">
                    {formatFileSize(storageStats.storageByType.images)}
                  </span>
                  <span className="text-xs text-gray-500 ml-2">
                    {getTypePercentage(storageStats.storageByType.images).toFixed(1)}%
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3" />
                  <span className="text-sm text-gray-600">Audio</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium text-gray-900">
                    {formatFileSize(storageStats.storageByType.audio)}
                  </span>
                  <span className="text-xs text-gray-500 ml-2">
                    {getTypePercentage(storageStats.storageByType.audio).toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Storage Activity</h3>
            <div className="space-y-3 max-h-40 overflow-y-auto">
              {storageStats.recentActivity.length === 0 ? (
                <p className="text-gray-500 text-sm">No recent activity</p>
              ) : (
                storageStats.recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-center justify-between py-1">
                    <div className="flex items-center">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 ${
                        activity.action === 'upload' 
                          ? 'bg-green-100' 
                          : 'bg-red-100'
                      }`}>
                        {activity.action === 'upload' ? (
                          <Upload className="w-3 h-3 text-green-600" />
                        ) : (
                          <Trash2 className="w-3 h-3 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 truncate max-w-32">{activity.fileName}</p>
                        <p className="text-xs text-gray-500">{formatDate(activity.timestamp)}</p>
                      </div>
                    </div>
                    <span className="text-sm text-gray-600">
                      {activity.action === 'delete' ? '-' : ''}{formatFileSize(activity.size)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Storage Information */}
        <div className="bg-white border rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Storage Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Maximum file size</span>
                <span className="text-sm font-medium text-gray-900">4 GB</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Company directory</span>
                <span className="text-sm font-mono text-gray-900">{storageStats.companyDirectory}</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Last updated</span>
                <span className="text-sm text-gray-900">{formatDate(storageStats.lastUpdated)}</span>
              </div>
            </div>
          </div>
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600 flex items-start">
              <Info className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
              Files are automatically organized by company and managed through the Assets page.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorageManagement;